import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { toJpeg } from "html-to-image";
import { QRCodeCanvas } from "qrcode.react";
import {
  Download, Loader2, MapPin, CheckCircle2, Phone, Mail, Sun, QrCode, Plus,
  Zap, Leaf, Landmark, TrendingUp, ShieldCheck, Clock, Lightbulb, Users, Share2,
} from "lucide-react";
import { INDIAN_STATES } from "../../lib/indianStates";
import { FLYER_LANGS, FLYER_I18N, FLYER_CATEGORY_I18N, BENEFIT_KEYS, bottomLabel } from "../../lib/flyerI18n";
import ScToolkitBack from "../../components/sc/ScToolkitBack";

const ROOFTOP_PHOTO = "https://static.prod-images.emergentagent.com/jobs/a8355e93-a976-4868-a629-726f7e154d45/images/d1e0b33d1fdca1a4c5a2c17fb5f7cc358663bff5a3b7ffee3908bfbe1a601d37.jpeg";
const HOUSING_PHOTO = "https://static.prod-images.emergentagent.com/jobs/a8355e93-a976-4868-a629-726f7e154d45/images/9a8f3c6ee14d26227ad88e80aaa99142a57b721469165e1036b910cde4ee9135.jpeg";
const STREETLIGHT_PHOTO = "https://images.unsplash.com/photo-1740805276608-ef60e2e468ba?crop=entropy&cs=srgb&fm=jpg&q=85";
const COMPANY_PHONE = "11 6926 8785";
const COMPANY_EMAIL = "support@purvodayasolar.com";

const BENEFIT_ICONS = {
  zero_bill: Zap,
  clean_energy: Leaf,
  subsidy: Landmark,
  property_value: TrendingUp,
  low_maintenance: ShieldCheck,
  quick_payback: Clock,
};

// Maps a translatable `iconKey` (stored in FLYER_CATEGORY_I18N) to its Icon component.
const ICON_MAP = { zap: Zap, leaf: Leaf, landmark: Landmark, trendingUp: TrendingUp, shieldCheck: ShieldCheck, clock: Clock };

// Category-specific poster variants — same flyer FORMAT (logo, hero photo, subsidy
// banner, Purvodaya Advantage checklist, benefit strip, QR footer) with all text
// content sourced from FLYER_CATEGORY_I18N (per selected language). The QR always
// carries `&category=` so the scan jumps straight to that category's pre-filled
// lead/service form — AND still tags the SC who shared it, exactly like the
// all-purpose flyer (continuation of the existing QR → SC logic).
const POSTER_CATEGORIES = [
  { value: "all", label: "Residential", Icon: Sun, photo: ROOFTOP_PHOTO },
  { value: "housing_society", label: "Housing Society", Icon: Users, photo: HOUSING_PHOTO },
  { value: "street_light", label: "Street Light", Icon: Lightbulb, photo: STREETLIGHT_PHOTO },
];

export default function ScFlyer() {
  const [flyer, setFlyer] = useState(null);
  const [lang, setLang] = useState("en");
  const [poster, setPoster] = useState("all");
  const [stateChoice, setStateChoice] = useState("");
  const [savingState, setSavingState] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const flyerRef = useRef(null);

  async function loadFlyerData() {
    try {
      const { data } = await api.get("/sc/flyer-data");
      setFlyer(data);
      setStateChoice(data.sc?.state || "");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  useEffect(() => { loadFlyerData(); }, []);

  async function saveState() {
    if (!stateChoice) return;
    setSavingState(true);
    try {
      await api.put("/sc/profile", { state: stateChoice });
      toast.success("State saved");
      await loadFlyerData();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingState(false);
    }
  }

  async function renderFlyerJpg() {
    return toJpeg(flyerRef.current, { quality: 0.95, pixelRatio: 2.5, backgroundColor: "#ffffff", skipFonts: true });
  }

  async function downloadJpg() {
    if (!flyerRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await renderFlyerJpg();
      const link = document.createElement("a");
      link.download = `purvodaya-solar-campaign-${poster}-${lang}.jpg`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      toast.error("Could not generate the image — please try again");
    } finally {
      setDownloading(false);
    }
  }

  async function shareJpg() {
    if (!flyerRef.current) return;
    setSharing(true);
    try {
      const dataUrl = await renderFlyerJpg();
      const blob = await (await fetch(dataUrl)).blob();
      const filename = `purvodaya-solar-campaign-${poster}-${lang}.jpg`;
      const file = new File([blob], filename, { type: "image/jpeg" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Purvodaya Solar" });
      } else {
        const link = document.createElement("a");
        link.download = filename;
        link.href = dataUrl;
        link.click();
        toast.info("Sharing isn't supported on this browser — image downloaded, attach it in WhatsApp");
      }
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Could not share the image — please try again");
    } finally {
      setSharing(false);
    }
  }

  if (!flyer) return <div className="font-mono text-xs text-[#475569] mt-10 text-center" data-testid="sc-flyer-loading">Loading…</div>;

  const t = FLYER_I18N[lang] || FLYER_I18N.en;
  const benefitKeys = (flyer.benefit_keys || []).length ? flyer.benefit_keys : BENEFIT_KEYS;
  const posterCfg = POSTER_CATEGORIES.find((p) => p.value === poster) || POSTER_CATEGORIES[0];
  const catT = (FLYER_CATEGORY_I18N[lang] && FLYER_CATEGORY_I18N[lang][poster]) || FLYER_CATEGORY_I18N.en[poster];
  const leadUrl = poster === "all" ? flyer.lead_url : `${flyer.lead_url}&category=${poster}`;
  const headlineLine1 = catT.headlineLine1 || t.headlineLine1;
  const headlineLine2 = catT.headlineLine2 || t.headlineLine2;
  const subheadline = catT.subheadline || t.subheadline || t.growthTagline;
  const paragraph = catT.paragraph || t.paragraph || "";
  const checklist = catT.checklist || t.checklist;
  const benefitStrip = catT.benefits
    ? catT.benefits.map((b) => ({ Icon: ICON_MAP[b.iconKey] || Zap, label: b.label }))
    : benefitKeys.map((k) => ({ Icon: BENEFIT_ICONS[k] || Zap, label: bottomLabel(lang, k, flyer.subsidy_amount) }));
  const subsidy = catT.subsidy || {
    eyebrow: t.subsidyScheme, big: `₹${Number(flyer.subsidy_amount || 0).toLocaleString("en-IN")}`, unit: "", footer: catT.subsidyFooter, showUpTo: true,
  };
  const subsidyBigDisplay = subsidy.showUpTo ? `${t.upToBefore || ""}${subsidy.big}${t.upToAfter || ""}` : subsidy.big;
  const badge = catT.badge;

  return (
    <div data-testid="sc-flyer-page" className="pb-4">
      <ScToolkitBack />
      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">Toolkit</div>
      <h1 className="text-2xl font-bold tracking-tight mt-1 text-slate-900">My Campaigns.</h1>
      <p className="text-sm text-slate-500 mt-1">Generate a premium, ready-to-share solar marketing campaign with your own QR referral link.</p>

      {!flyer.sc?.state && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3.5" data-testid="sc-flyer-state-prompt">
          <div className="text-xs font-semibold text-amber-800">Set your state for accurate benefits</div>
          <div className="flex items-center gap-2 mt-2">
            <select value={stateChoice} onChange={(e) => setStateChoice(e.target.value)} data-testid="sc-flyer-state-select"
              className="flex-1 min-w-0 border border-amber-300 rounded-lg px-2.5 py-2 text-sm bg-white text-slate-900">
              <option value="">Select state…</option>
              {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={saveState} disabled={savingState || !stateChoice} data-testid="sc-flyer-state-save"
              className="shrink-0 bg-[#B45309] text-white text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-lg disabled:opacity-40">
              {savingState ? "…" : "Save"}
            </button>
          </div>
          <div className="text-[10.5px] text-amber-700 mt-1.5">Using default India-wide benefits until then.</div>
        </div>
      )}

      {/* Poster category tabs — swaps the whole flyer content, same format */}
      <div className="flex items-center gap-1.5 overflow-x-auto mt-4 pb-1" data-testid="sc-flyer-poster-row">
        {POSTER_CATEGORIES.map((p) => (
          <button
            key={p.value}
            onClick={() => setPoster(p.value)}
            data-testid={`sc-flyer-poster-${p.value}`}
            className={"flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors " +
              (poster === p.value ? "bg-[#B45309] text-white border-[#B45309]" : "border-slate-200 text-slate-600 bg-white")}
          >
            <p.Icon className="w-3.5 h-3.5" /> {p.label}
          </button>
        ))}
      </div>

      {/* Language selector */}
      <div className="flex items-center gap-1.5 overflow-x-auto mt-2 pb-1" data-testid="sc-flyer-lang-row">
        {FLYER_LANGS.map((l) => (
          <button
            key={l.code}
            onClick={() => setLang(l.code)}
            data-testid={`sc-flyer-lang-${l.code}`}
            className={"flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors " +
              (lang === l.code ? "bg-[#1B5E20] text-white border-[#1B5E20]" : "border-slate-200 text-slate-600 bg-white")}
          >
            {l.label}
          </button>
        ))}
      </div>

      {/* Flyer preview */}
      <div className="mt-4 flex justify-center">
        <div
          ref={flyerRef}
          data-testid="sc-flyer-preview"
          style={{ width: 380, background: "#fff", fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif", overflow: "hidden" }}
          className="shadow-lg rounded-lg"
        >
          {/* Header: logo lockup (arc + wordmark baked into the asset) + accent bar */}
          <div style={{ padding: "20px 22px 12px" }}>
            <img src="/purvodaya-logo.png" alt="Purvodaya Solar" style={{ height: 52, width: "auto", display: "block" }} />
          </div>
          <div style={{ height: 4, background: "linear-gradient(to right, #F97316 0%, #F97316 12%, #15803D 12%, #15803D 100%)" }} />

          {/* Headline block */}
          <div style={{ padding: "16px 22px 0" }}>
            <div style={{ fontSize: 25, fontWeight: 900, lineHeight: 1.08, color: "#1E3A8A", textTransform: "uppercase" }}>{headlineLine1}</div>
            <div style={{ fontSize: 25, fontWeight: 900, lineHeight: 1.08, color: "#15803D", textTransform: "uppercase" }}>{headlineLine2}</div>
            <div style={{ height: 3.5, width: 84, background: "linear-gradient(to right, #F97316 0%, #F97316 40%, #15803D 40%, #15803D 100%)", margin: "10px 0 9px", borderRadius: 2 }} />
            <div style={{ fontSize: 13, fontWeight: 800, color: "#1E293B", lineHeight: 1.3 }}>{subheadline}</div>
            {paragraph && <div style={{ fontSize: 10.5, fontWeight: 600, color: "#64748B", lineHeight: 1.45, marginTop: 6 }}>{paragraph}</div>}
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12 }}>
              <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#F97316", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Plus size={11} color="white" strokeWidth={3} />
              </div>
              <div style={{ fontSize: 10, fontWeight: 900, color: "#1E3A8A", letterSpacing: 0.8 }}>{catT.tag}</div>
            </div>
          </div>

          {/* Full-bleed hero photo with soft fade into the white page */}
          <div style={{ position: "relative", marginTop: 14, height: 190, overflow: "hidden" }}>
            <img src={posterCfg.photo} crossOrigin="anonymous" alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, white 0%, rgba(255,255,255,0) 22%)" }} />
          </div>

          {/* Subsidy / benefit banner */}
          <div style={{ margin: "14px 22px 0" }}>
            <div style={{ background: "#1E3A8A", borderRadius: "12px 12px 0 0", padding: "11px 12px 5px", textAlign: "center" }}>
              <div style={{ color: "white", fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase" }}>{subsidy.eyebrow}</div>
              <div style={{ color: "#FACC15", fontSize: subsidyBigDisplay.length > 13 ? 20 : subsidyBigDisplay.length > 9 ? 26 : 32, fontWeight: 900, lineHeight: 1.15, marginTop: 2, whiteSpace: "nowrap" }}>
                {subsidyBigDisplay}
              </div>
              {subsidy.unit && <div style={{ color: "#FACC15", fontSize: 13, fontWeight: 800, letterSpacing: 0.5 }}>{subsidy.unit}</div>}
            </div>
            <div style={{ background: "#15803D", borderRadius: "0 0 12px 12px", padding: "7px 10px", textAlign: "center" }}>
              <div style={{ color: "white", fontSize: 10.5, fontWeight: 800, letterSpacing: 0.2 }}>{subsidy.footer}</div>
            </div>
          </div>
          {poster === "all" && flyer.state_topup_amount > 0 && flyer.sc?.state && (
            <div style={{ textAlign: "center", padding: "6px 22px 0" }}>
              <span style={{ background: "#FEF3C7", color: "#92400E", fontSize: 9.5, fontWeight: 800, padding: "3px 10px", borderRadius: 999 }}>
                {t.stateTopupLabel(flyer.sc.state, flyer.state_topup_amount)}
              </span>
            </div>
          )}
          {catT.disclaimer && (
            <div style={{ fontSize: 8.5, fontStyle: "italic", color: "#94A3B8", textAlign: "center", padding: "5px 22px 0", lineHeight: 1.35 }}>{catT.disclaimer}</div>
          )}

          {/* Purvodaya Advantage: checklist + scheme badge */}
          <div style={{ padding: "16px 22px 0" }}>
            <div style={{ fontSize: 9.5, fontWeight: 900, color: "#15803D", letterSpacing: 1, textTransform: "uppercase" }}>Purvodaya {t.advantageWord}</div>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#1E293B", marginTop: 3, lineHeight: 1.25 }}>{t.reliableSupportTagline}</div>
          </div>
          <div style={{ display: "flex", padding: "11px 22px 0", gap: 10, alignItems: "flex-start" }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
              {checklist.map((item, i) => (
                <div key={i} style={{ display: "flex", gap: 5, fontSize: 10, color: "#1E293B", fontWeight: 700, alignItems: "flex-start" }}>
                  <CheckCircle2 size={11.5} color="#15803D" style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <div style={{
              width: 84, height: 84, borderRadius: "50%", border: "2.5px solid #15803D", flexShrink: 0,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              textAlign: "center", background: "white", padding: 4,
            }}>
              <Sun size={16} color="#F97316" />
              <div style={{ fontSize: 7.2, fontWeight: 900, color: "#15803D", lineHeight: 1.1, marginTop: 3 }}>
                {badge[0]}<br />{badge[1]}
              </div>
            </div>
          </div>

          {/* Bottom benefit icons row */}
          <div style={{ display: "flex", padding: "18px 10px 4px", gap: 2 }}>
            {benefitStrip.map((b, i) => (
              <div key={i} style={{ flex: 1, textAlign: "center", padding: "0 2px" }}>
                <div style={{ width: 34, height: 34, borderRadius: "50%", border: "1.5px solid #15803D", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto" }}>
                  <b.Icon size={16} color="#15803D" />
                </div>
                <div style={{ fontSize: 6.8, marginTop: 4, fontWeight: 700, color: "#334155", whiteSpace: "pre-line", lineHeight: 1.15 }}>{b.label}</div>
              </div>
            ))}
          </div>

          {/* Footer: dark navy block — heading, contact, QR (QR always tagged to this SC) */}
          <div style={{ background: "#1E293B", padding: "22px 22px 20px", marginTop: 18 }}>
            <div style={{ fontSize: 9.5, fontWeight: 900, color: "#FB923C", letterSpacing: 1.2, textTransform: "uppercase" }}>{t.letsTalkHeading}</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: "white", marginTop: 4, lineHeight: 1.25 }}>{catT.footerHeading}</div>
            <div style={{ fontSize: 10, fontWeight: 600, color: "#94A3B8", marginTop: 5, lineHeight: 1.45 }}>
              {t.qrScanDesc}
            </div>
            <div style={{ height: 1, background: "rgba(255,255,255,0.15)", margin: "16px 0 14px" }} />
            <div style={{ fontSize: 8.5, fontWeight: 800, color: "#64748B", letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>{t.additionalEnquiries}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 6 }}>
              <Phone size={12} color="#FB923C" style={{ flexShrink: 0 }} />
              <div style={{ fontSize: 11, color: "white", fontWeight: 700 }}>{COMPANY_PHONE}</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 16 }}>
              <Mail size={12} color="#FB923C" style={{ flexShrink: 0 }} />
              <div style={{ fontSize: 11, color: "white", fontWeight: 700 }}>{COMPANY_EMAIL}</div>
            </div>
            <div style={{
              background: "white", padding: "10px 10px 9px", borderRadius: 14, textAlign: "center", width: 140, margin: "0 auto",
              border: "2.5px solid #F97316", boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3, marginBottom: 6 }}>
                <QrCode size={9} color="#F97316" />
                <div style={{ fontSize: 7.5, fontWeight: 900, color: "#F97316", letterSpacing: 0.3, textTransform: "uppercase" }}>{t.ctaQr}</div>
              </div>
              <QRCodeCanvas value={leadUrl} size={108} />
            </div>
          </div>
          <div style={{ textAlign: "center", padding: "9px 18px", fontSize: 6.8, color: "#CBD5E1", fontWeight: 600, letterSpacing: 0.2, background: "#1E293B" }}>{t.footerTagline}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2.5 mt-5">
        <button
          onClick={downloadJpg}
          disabled={downloading}
          data-testid="sc-flyer-download-btn"
          className="bg-[#1B5E20] hover:bg-[#15501A] text-white py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 shadow-sm active:scale-[0.99] transition-all"
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {downloading ? "Generating…" : "Download"}
        </button>
        <button
          onClick={shareJpg}
          disabled={sharing}
          data-testid="sc-flyer-share-btn"
          className="bg-[#128C7E] hover:bg-[#0E6F63] text-white py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 shadow-sm active:scale-[0.99] transition-all"
        >
          {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
          {sharing ? "Preparing…" : "Share"}
        </button>
      </div>

      {flyer.sc?.state && (
        <div className="flex items-center gap-1.5 justify-center text-[11px] text-slate-400 mt-3" data-testid="sc-flyer-state-tag">
          <MapPin className="w-3 h-3" /> Benefits shown for {flyer.sc.state}
          {!flyer.is_default_benefit && <CheckCircle2 className="w-3 h-3 text-[#1B5E20]" />}
        </div>
      )}
    </div>
  );
}
